/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback } from "react";
import MainLayout from "../../layouts/MainLayout";
import { useNavigate } from "react-router-dom";
import "../../assets/styles/custom-styles.css";
import useForm from "../../hooks/useForm";
import {
  normalizeFormErrors,
  setDefaultFieldValues,
} from "../../utils/objectUtils";
import { roleFields } from "../../constants/forms/roleFields";
import Form from "../../components/forms/Form";
import useBaseService from "../../hooks/services/useBaseService";
import { entities } from "../../constants/entities";
import PageHeader from "../../components/PageHeader";
import SimpleBody from "../../components/forms/SimpleBody";
import SectionHeader from "../../components/forms/SectionHeader";
import useRemoteSelectOptions from "../../hooks/useRemoteSelectOptions";
import { transformFiltersForUrl } from "../../utils/transformFiltersForUrl";
import { useMutation } from "@tanstack/react-query";

const CreateRolePage = () => {
  const navigate = useNavigate();
  const { formData, resetForm, handleChange, setFormData } = useForm(
    setDefaultFieldValues(roleFields),
  );
  const {
    formErrors,
    post: create,
    get: baseServiceGet,
  } = useBaseService(navigate);

  const permissionSelect = useRemoteSelectOptions({
    queryKey: ["permissions"],
    fetcher: async ({ page = 1, perPage = 15, search = "" }) => {
      const filters = transformFiltersForUrl({
        ...(search && { name: search }),
      });
      const params = { filters, page, perPage };

      const response = await baseServiceGet(entities.permissions.get, params);
      return response.result;
    },
    fieldId: "permissions",
    formData,
    mapToOption: (permission) => ({
      value: permission.id,
      label: permission.name,
    }),
    setFormData,
    perPage: 15,
    initialPage: 1,
    enablePagination: true,
  });

  const selects = {
    permissions: permissionSelect,
  };

  const getSelectMeta = useCallback(
    (fieldId) => {
      const selectConfig = selects[fieldId];
      if (!selectConfig) return {};

      return {
        hasNextPage: selectConfig.hasNextPage,
        isFetchingNextPage: selectConfig.isFetchingNextPage,
        isLoading: selectConfig.isLoading,
        isFetching: selectConfig.isFetching,
        handleMenuScrollToBottom: selectConfig.handleMenuScrollToBottom,
        handleSearch: selectConfig.handleSearch,
        onFocus: selectConfig.activateQuery,
      };
    },
    [selects],
  );

  // --- Funções para Selects (getOptions e getSelectedValue) ---
  const getOptions = useCallback(
    (fieldId) => {
      const selectConfig = selects[fieldId];
      return selectConfig?.options || [];
    },
    [selects],
  );

  const getSelectedValue = useCallback(
    (fieldId) => {
      const selectConfig = selects[fieldId];
      return selectConfig?.selectedValue || null;
    },
    [selects],
  );

  const createRoleMutation = useMutation({
    mutationFn: (data) => create(entities.roles.create, data),
    onSuccess: () => {
      resetForm();
      navigate("/aluguel/cargos");
    },
  });

  const handleSubmit = async () => {
    try {
      await createRoleMutation.mutateAsync(formData);
    } catch (error) {
      console.log(error);
    }
  };

  const handleBack = () => {
    navigate("/aluguel/cargos");
  };

  return (
    <MainLayout selectedCompany="ALUCOM">
      <PageHeader
        title="Cadastro de Cargo"
        showBackButton={true}
        backUrl="/aluguel/cargos"
      />
      <div className="container-fluid p-1">
        <Form
          onSubmit={handleSubmit}
          initialFormData={formData}
          handleBack={handleBack}
          textSubmit="Cadastrar"
          textLoadingSubmit="Cadastrando..."
        >
          {() => {
            const renderedSections = new Set();

            return roleFields.map((section, index) => {
              const sectionType = section.section.split(" - ")[0];
              const showHeader = !renderedSections.has(sectionType);

              if (showHeader) {
                renderedSections.add(sectionType);
              }

              return (
                <div key={section.section + " " + index}>
                  {showHeader && (
                    <SectionHeader
                      section={section.section}
                      viewTable={false}
                      setViewTable={() => {}}
                      addFieldsInData={() => {}}
                    />
                  )}

                  <SimpleBody
                    fields={section.fields}
                    formErrors={normalizeFormErrors(formErrors)}
                    formData={formData}
                    handleFieldChange={handleChange}
                    getOptions={getOptions}
                    getSelectedValue={getSelectedValue}
                    getSelectMeta={getSelectMeta}
                  />
                </div>
              );
            });
          }}
        </Form>
      </div>
    </MainLayout>
  );
};

export default CreateRolePage;
